/**
 * @fileoverview The two moments that lead a gardener to sharing, passed between screens.
 *
 * The first time a plant gets a photograph, Grow asks once whether to share
 * it: the moment of most pride and least effort. And a plant added without a
 * photograph gets an offer to add one, since a plant with no photograph
 * cannot be shared. Either can start on one screen (the photo tray, the add
 * form) and be answered on the Plants page, so the pending plant is kept in
 * the browser (plumbing between screens) and announced with an event.
 *
 * Asked once per account, not per browser: whether to ask is the server's
 * (the first_share advice in the bottom panel, placed until it is answered;
 * its seen, done and put away go through POST /todo/marks from
 * ShareMoments), so an answered question stays answered on a new browser
 * (audit A70). Nothing here remembers the answer; a pending plant is
 * forgotten once answered, or once the server places no question.
 *
 * Where: noteFirstPhoto from the upload dialog and Add photos; offerPhoto from
 * the tips (useTipAction, the after-action tip); ShareMoments listens;
 * DoctorMoments waits while a share question is pending (pendingFirstShare).
 * API: none; window events and localStorage.
 * Settings: ADD_PHOTO_EVENT, FIRST_SHARE_EVENT,
 * FIRST_SHARE_PENDING_STORAGE_KEY (config/tunables/garden).
 *
 * @module shareMoments
 */

import {
  ADD_PHOTO_EVENT, FIRST_SHARE_EVENT, FIRST_SHARE_PENDING_STORAGE_KEY as PENDING_KEY,
} from '@/config/tunables/garden';

// The event names, from here as before, for the page that listens for them.
export { ADD_PHOTO_EVENT, FIRST_SHARE_EVENT };

/**
 * A value kept in this browser, or null when there is none or storage is closed to us.
 *
 * @param key - The storage key.
 */
const read = (key: string): string | null => {
  try { return window.localStorage.getItem(key); } catch { return null; }
};
/**
 * Keeps a value in this browser (null removes it); quietly nothing where storage is closed.
 *
 * @param key - The storage key.
 * @param value - What to keep, or null to remove it.
 */
const write = (key: string, value: string | null): void => {
  try {
    if (value === null) window.localStorage.removeItem(key); else window.localStorage.setItem(key, value);
  } catch { /* private mode: the moment is simply not kept */ }
};

/**
 * A plant has its first photograph: hand it to the Plants page, which asks
 * only if the server places the question.
 *
 * @param plantObjId - The plant.
 */
export function noteFirstPhoto(plantObjId: number): void {
  write(PENDING_KEY, String(plantObjId));
  window.dispatchEvent(new CustomEvent(FIRST_SHARE_EVENT, { detail: plantObjId }));
}

/**
 * The plant waiting to be asked about, if any.
 *
 * @returns Its id, or null.
 */
export function pendingFirstShare(): number | null {
  const raw = read(PENDING_KEY);
  const id = raw ? Number(raw) : NaN;
  return Number.isFinite(id) ? id : null;
}

/** Answered, or not to be asked: the pending plant is forgotten (the answer itself is the server's mark). */
export function firstShareAnswered(): void {
  write(PENDING_KEY, null);
}

/**
 * Opens the photo screen for a plant, wherever the Plants page is listening.
 *
 * @param plantObjId - The plant.
 */
export function offerPhoto(plantObjId: number): void {
  window.dispatchEvent(new CustomEvent(ADD_PHOTO_EVENT, { detail: plantObjId }));
}
